<script setup lang="ts">
  import { onUnmounted, toRef } from "vue";
  import { storeToRefs } from "pinia";
  import { Spinner } from "@/components/ui/spinner";
  import ReviewCard from "@/components/Survey/Review/ReviewPane/ReviewCard/ReviewCard.vue";
  import { useSurveyCardsStore } from "@stores/survey/surveyCards";
  import { useSurveySectionsStore } from "@stores/survey/surveySections";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useReviewDocument, isLogicCard } from "@utils/composables/reviewDocument";

  const surveyCardsStore = useSurveyCardsStore();
  const { firstLoadReviewPage } = storeToRefs(surveyCardsStore);
  const { sections } = storeToRefs(useSurveySectionsStore());
  const { surveyName } = storeToRefs(useSurveyStore());

  const props = defineProps<{
    cards: Array<Record<string, unknown>>;
    cardSubTypesInfos: Record<string, unknown>;
    cardTypesGroups: Array<Record<string, unknown>>;
    showOptions?: boolean;
    showAnswers?: boolean;
    showConditions?: boolean;
  }>();

  const { entries } = useReviewDocument(toRef(props, "cards"), sections);

  const hasRuleAbove = (index: number) => {
    const entry = entries.value[index];
    const previous = entries.value[index - 1];
    if (!previous || entry.startsSection) return false;
    return !isLogicCard(entry.card) && !isLogicCard(previous.card);
  };

  onUnmounted(() => {
    firstLoadReviewPage.value = true;
  });
</script>

<template>
  <article
    data-cy="review-pane"
    class="mx-auto w-full max-w-170 px-10 pt-12 pb-14">
    <header class="mb-2 border-b pb-7">
      <h1 class="text-2xl font-bold tracking-[-0.02em] text-primary">{{ surveyName }}</h1>
    </header>

    <Suspense>
      <template #default>
        <div>
          <div
            v-for="(entry, index) in entries"
            :key="entry.card.token">
            <h2
              v-if="entry.startsSection && entry.sectionName"
              class="mt-12 mb-3 border-b pb-2 text-lg font-semibold tracking-[-0.01em] text-primary first:mt-2">
              {{ entry.sectionName }}
            </h2>
            <div :class="hasRuleAbove(index) && 'border-t'">
              <ReviewCard
                :card="entry.card"
                :card-sub-types-infos="cardSubTypesInfos"
                :card-types-groups="cardTypesGroups"
                :show-options="showOptions"
                :show-answers="showAnswers"
                :show-conditions="showConditions" />
            </div>
          </div>
        </div>
      </template>
      <template #fallback>
        <Spinner class="mx-auto my-2 size-6" />
      </template>
    </Suspense>
  </article>
</template>
